<template>
    <div class="suite-container min-vh-100 d-flex flex-column align-items-center py-5">
        <div class="text-center mb-5 fade-in-up">
            <h1 class="display-4 fw-bold text-gradient">Codifyo Suite</h1>
            <p class="text-muted fs-5 mt-2">Votre espace de productivité centralisé</p>
        </div>

        <div v-if="loading" class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Chargement...</span>
        </div>
        
        <div v-else-if="error" class="alert alert-danger" role="alert">
            {{ error }}
        </div>

        <div v-else class="container max-w-4xl">
            <div class="row g-4 justify-content-center">
                <div v-for="app in apps" :key="app.key" class="col-12 col-sm-6 col-lg-4 fade-in-up" :style="{ 'animation-delay': `${index * 0.1}s` }">
                    <a :href="app.url" class="app-card text-decoration-none h-100 d-flex flex-column align-items-center justify-content-center p-4">
                        <div class="app-icon mb-3 d-flex align-items-center justify-content-center">
                            <i :class="getAppIcon(app.key) + ' fa-2x'"></i>
                        </div>
                        <h3 class="app-name fs-4 fw-semibold m-0">{{ app.name }}</h3>
                        <div class="hover-indicator mt-3">
                            <i class="fa-solid fa-arrow-right"></i>
                        </div>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useFetch } from '@vueuse/core'
import { useGlobalState } from '@/composables/useGlobalState'

interface AppInfo {
    key: string;
    name: string;
    url: string;
}

const apps = ref<AppInfo[]>([])
const loading = ref(true)
const error = ref<string | null>(null)
const { getAuthHeader } = useGlobalState()

const getAppIcon = (key: string) => {
    const icons: Record<string, string> = {
        finance: 'fa-solid fa-chart-line text-success',
        toolbox: 'fa-solid fa-toolbox text-primary',
        sport: 'fa-solid fa-person-running text-warning',
        betting: 'fa-solid fa-dice text-danger',
        shapearchitect3d: 'fa-solid fa-cube text-info',
        plans: 'fa-solid fa-building text-secondary'
    }
    return icons[key] || 'fa-solid fa-window-maximize text-muted'
}

onMounted(async () => {
    try {
        const { data, statusCode } = await useFetch('/api/suite/apps', {
            headers: getAuthHeader()
        }).json()
        
        if (statusCode.value === 200) {
            apps.value = data.value
        } else {
            error.value = "Impossible de charger les applications."
        }
    } catch (e) {
        error.value = "Une erreur est survenue lors de la connexion au serveur."
        console.error(e)
    } finally {
        loading.value = false
    }
})
</script>

<style scoped>
.suite-container {
    background-color: #0f172a;
    color: #f8fafc;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
}

.text-gradient {
    background: linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.max-w-4xl {
    max-width: 1000px;
}

.app-card {
    background: rgba(30, 41, 59, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 1.5rem;
    backdrop-filter: blur(10px);
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    color: #f8fafc;
    position: relative;
    overflow: hidden;
}

.app-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
}

.app-card:hover {
    transform: translateY(-10px) scale(1.02);
    border-color: rgba(96, 165, 250, 0.5);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(96, 165, 250, 0.2);
}

.app-card:hover::before {
    opacity: 1;
}

.app-icon {
    width: 80px;
    height: 80px;
    border-radius: 1rem;
    background: rgba(15, 23, 42, 0.8);
    box-shadow: inset 0 2px 4px rgba(255,255,255,0.05), 0 4px 10px rgba(0,0,0,0.3);
    transition: transform 0.3s ease;
}

.app-card:hover .app-icon {
    transform: scale(1.1) rotate(5deg);
}

.hover-indicator {
    opacity: 0;
    transform: translateX(-15px);
    transition: all 0.3s ease;
    color: #60a5fa;
}

.app-card:hover .hover-indicator {
    opacity: 1;
    transform: translateX(0);
}

/* Animations */
.fade-in-up {
    animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    opacity: 0;
    transform: translateY(30px);
}

@keyframes fadeInUp {
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
